import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/CustomAuthContext';
import { useFeatureFlags, FeatureFlags } from '../../contexts/FeatureFlagsContext';
import './HomePage.css';

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
  color: string;
  badge?: string;
}

const FeatureCard: React.FC<FeatureCardProps & { onClick: () => void }> = ({
  icon, title, description, color, badge, onClick,
}) => (
  <div className={`home-feature-card home-feature-card--${color}`} onClick={onClick}>
    {badge && <span className="home-feature-badge">{badge}</span>}
    <div className="home-feature-icon">{icon}</div>
    <h3 className="home-feature-title">{title}</h3>
    <p className="home-feature-desc">{description}</p>
    <span className="home-feature-cta">Open →</span>
  </div>
);

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { flags, isEnabled } = useFeatureFlags();
  const isAdmin = user?.role === 'admin';

  const go = (path: string) => navigate(path);
  const openExternal = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

  // Helper so admin can always see every card (even disabled ones shown greyed out)
  const cardEnabled = (key: keyof FeatureFlags) => isEnabled(key, isAdmin);
  const cardVisible = (key: keyof FeatureFlags) => isAdmin || flags[key];

  const features: Array<{
    key: keyof FeatureFlags;
    icon: string;
    title: string;
    description: string;
    color: string;
    badge?: string;
    action: () => void;
  }> = [
    {
      key: 'feature_home_price_history',
      icon: '📊',
      title: 'Price History',
      description:
        'Browse historical auction data for every Pokemon and item in PRO. Filter by nature, IVs, date, and more.',
      color: 'blue',
      action: () => go('/price-history'),
    },
    {
      key: 'feature_home_map',
      icon: '🗺️',
      title: 'Interactive Map',
      description:
        'Explore the PRO world map with spawn locations for over 600 Pokemon. Filter by region, time of day, and rarity.',
      color: 'green',
      action: () => go('/map'),
    },
    {
      key: 'feature_home_damage_calc',
      icon: '⚔️',
      title: 'Damage Calculator',
      description:
        'Calculate exact damage ranges for any move combination. Supports all natures, IVs, EVs, and weather conditions.',
      color: 'red',
      action: () => go('/damage-calculator'),
    },
    {
      key: 'feature_home_faq',
      icon: '📖',
      title: 'FAQ & Guide',
      description:
        'Answers to common questions about PRO pricing, submission guidelines, and how to use the tracker.',
      color: 'purple',
      action: () => go('/faq'),
    },
  ];

  const visibleFeatures = features.filter((f) => cardVisible(f.key));

  return (
    <div className="home-page">
      {/* ── Hero ── */}
      <section className="home-hero">
        <div className="home-hero__glow home-hero__glow--left" />
        <div className="home-hero__glow home-hero__glow--right" />
        <div className="home-hero__content">
          <div className="home-hero__eyebrow">Pokemon Revolution Online</div>
          <h1 className="home-hero__title">
            Your PRO{' '}
            <span className="home-hero__title--accent">Market Hub</span>
          </h1>
          <p className="home-hero__sub">
            Price history, spawn maps, damage calculations, and a Discord bot —
            everything you need to master the PRO economy in one place.
          </p>
          <div className="home-hero__actions">
            <button
              className="home-btn home-btn--primary"
              onClick={() => go('/price-history')}
            >
              Browse Prices
            </button>
            <button
              className="home-btn home-btn--ghost"
              onClick={() => openExternal('https://discord.gg/kteFmyZEDY')}
            >
              Join Discord
            </button>
          </div>
        </div>
        <div className="home-hero__icon" aria-hidden="true">
          <img src="/icon.png" alt="PRO Market History" className="home-hero__icon-img" />
        </div>
      </section>

      {/* ── Features Grid ── */}
      {visibleFeatures.length > 0 && (
        <section className="home-features">
          <div className="home-features__header">
            <h2>Everything you need</h2>
            <p>Powerful tools built for PRO players</p>
          </div>
          <div className={`home-features__grid home-features__grid--${Math.min(visibleFeatures.length, 3)}`}>
            {features.map((f) => {
              if (!cardVisible(f.key)) return null;
              const enabled = cardEnabled(f.key);
              return (
                <div
                  key={f.key}
                  className={!enabled ? 'home-feature-card-wrapper--disabled' : ''}
                  title={!enabled && isAdmin ? 'Feature disabled (admins can still access)' : undefined}
                >
                  <FeatureCard
                    {...f}
                    onClick={enabled ? f.action : () => {}}
                  />
                  {!enabled && isAdmin && (
                    <div className="home-feature-disabled-badge">Disabled</div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Discord CTA ── */}
      <section className="home-discord">
        <div className="home-discord__inner">
          <div className="home-discord__text">
            <h2>Join the community</h2>
            <p>
              Connect with thousands of PRO players. Get price alerts, spawn
              notifications, and trading tips directly in Discord.
            </p>
          </div>
          <div className="home-discord__actions">
            <button
              className="home-btn home-btn--discord"
              onClick={() => openExternal('https://discord.gg/kteFmyZEDY')}
            >
              <span className="home-btn__icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
                </svg>
              </span>
              Join our Discord
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
